// Note Expander v2, the web page's pure logic (PR 5-UI-1, O191), kept out of the components so
// a test drives it directly (tests/unit/v3/note-expander-v2-lib.test.js).
//
//   The page offers only what the server serves: the style choices come from GET /v2/me/prefs
//   (`choices`, read from the pack), the models from GET /v2/models, the words for every reason
//   code from GET /v2/copy. What lives here is presentation: labels, the inbox join, a run's
//   state in words, the poll schedule, and the once-per-session fallback notice.

// The pack's style values in words (DESIGN §5.3; mockup "prefs"). A value the pack adds later
// shows as itself until it is labelled here; the copy pin fails CI first.
const OPTION_LABELS = Object.freeze({
  verbosity: Object.freeze({ label: 'Length', values: Object.freeze({ concise: 'Concise', standard: 'Standard', detailed: 'Detailed' }) }),
  hpi: Object.freeze({ label: 'HPI', values: Object.freeze({ paragraph: 'Paragraph', bullets: 'Bullets' }) }),
  ap: Object.freeze({ label: 'Assessment and Plan', values: Object.freeze({ paragraph: 'Paragraph', bulleted: 'Bulleted plan' }) }),
  abbreviations: Object.freeze({ label: 'Abbreviations', values: Object.freeze({ expand: 'Expand', keep_common: 'Keep common' }) }),
  exam_layout: Object.freeze({ label: 'Exam layout', values: Object.freeze({ per_system: 'One line per system', grouped: 'Grouped' }) }),
});

const MODE_LABELS = Object.freeze({ grounded: 'Grounded', maximal: 'Maximal' });
const PROVIDER_LABELS = Object.freeze({ bedrock: 'Bedrock', openai: 'OpenAI' });

function optionLabel(key, value) {
  const o = OPTION_LABELS[key];
  return (o && o.values[value]) || String(value);
}
function optionName(key) {
  return (OPTION_LABELS[key] && OPTION_LABELS[key].label) || String(key);
}
function modeLabel(mode) { return MODE_LABELS[mode] || String(mode); }

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// 'YYYY-MM-DD' → 'Jan 14, 2027', read from the string itself. new Date('2027-01-14') is UTC
// midnight, which a browser in New York prints as the day before.
function formatIsoDate(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return '';
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  return `${MONTHS[month - 1]} ${day}, ${m[1]}`;
}

// One /models entry → the picker's words: name (provider) · marks · retires … · practice default
function modelOptionLabel(entry, practiceDefault) {
  const parts = [`${entry.display} (${PROVIDER_LABELS[entry.provider] || entry.provider})`];
  for (const mark of entry.marks || []) if (mark && mark.label) parts.push(mark.label);
  if (entry.retires) parts.push(`retires ${formatIsoDate(entry.retires)}`);
  if (practiceDefault && entry.alias === practiceDefault) parts.push('practice default');
  return parts.join(' · ');
}

// The inbox's rows (v1's GET /inbox) joined to their latest v2 run (GET /v2/runs/latest).
function mergeInbox(drafts, runs) {
  const byDraft = new Map((runs || []).map((r) => [String(r.draftId), r]));
  return (drafts || []).map((d) => ({ ...d, run: byDraft.get(String(d.draftId)) || null }));
}

// draftIds in chunks the route accepts (≤ 300 per call).
function chunk(ids, size = 300) {
  const out = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

const TERMINAL = Object.freeze(['ready', 'needs_attention', 'failed', 'superseded']);
function isTerminal(status) { return TERMINAL.includes(status); }

function copyFor(copy, group, code) {
  const g = copy && copy[group];
  return (g && code && typeof g[code] === 'string') ? g[code] : null;
}

const REVISION_WORDS = Object.freeze({
  accepted: 'Accepted. Writing to the chart…',
  written: 'Written to the chart',
  signed: 'Signed',
  discarded: 'Discarded',
});

// A draft's latest run → { kind, label } for its card. kind: none | running | ready | attention |
// failed | done. A superseded run reads as none: a newer request replaced it.
function rowState(run, copy) {
  const s = baseRowState(run, copy);
  // A swept draft (PR 7b) says so: the sweep writes it Grounded (O195), the web only reviews it.
  return run && run.source === 'sweep' && s.label ? { ...s, label: `Swept · ${s.label}` } : s;
}

function baseRowState(run, copy) {
  if (!run || run.status === 'superseded') return { kind: 'none', label: '' };
  if (run.status === 'queued' || run.status === 'running') return { kind: 'running', label: 'Expanding…' };
  if (run.status === 'needs_attention') {
    return { kind: 'attention', label: copyFor(copy, 'run', run.statusReason) || 'Needs attention. Nothing was written.' };
  }
  if (run.status === 'failed') {
    return { kind: 'failed', label: copyFor(copy, 'run', run.statusReason) || 'The expansion failed. Expand again.' };
  }
  const rev = run.revisionStatus;
  if (rev && REVISION_WORDS[rev]) return { kind: 'done', label: REVISION_WORDS[rev] };
  const open = Number(run.openHighlights) || 0;
  if (run.mode === 'maximal' && open > 0) {
    return { kind: 'ready', label: `${modeLabel(run.mode)} · ${open} highlight${open === 1 ? '' : 's'} to review` };
  }
  return { kind: 'ready', label: `${modeLabel(run.mode)} · ready to review` };
}

// The poll schedule for one run: 1 s, growing by half each time, at most 5 s apart, and given
// up after 10 minutes (the card then says to refresh).
const POLL_BUDGET_MS = 10 * 60 * 1000;
function nextPollDelay(attempt) {
  return Math.min(5000, Math.round(1000 * Math.pow(1.5, Math.max(0, attempt))));
}

// The PUT /v2/me/prefs body from the page's settings (exactly the fields the server validates).
function prefsBody(settings) {
  const s = settings || {};
  const notes = typeof s.notes === 'string' ? s.notes : null;
  return {
    options: { ...(s.options || {}) },
    defaultMode: s.defaultMode,
    defaultModel: s.defaultModel || null,
    notes: notes && notes.trim() ? notes : null,
  };
}

// Code points, as the server counts the notes limit ([...notes].length).
function charCount(text) { return [...String(text || '')].length; }

// A refused save names its field (400 { field }); these are its words.
function fieldMessage(field, notesMaxChars) {
  if (field === 'notes') return `Style notes must be ${Number(notesMaxChars || 1000).toLocaleString('en-US')} characters or fewer, and can't contain the expander's reserved markers.`;
  if (field === 'defaultModel') return 'That model is not available any more. Choose another.';
  if (field === 'defaultMode') return 'Choose Grounded or Maximal.';
  if (field && field.startsWith('options.')) return 'That choice is not available any more. Choose another.';
  return 'These preferences could not be saved.';
}

// The fallback notice is shown once per session for each saved model that could not be honoured.
// Storage can be missing or throw (a private window, blocked site data): then it shows each time.
function fellBackKey(savedModel) { return `ne2.fellBack.${savedModel || '-'}`; }
function fellBackSeen(storage, savedModel) {
  try { return !!(storage && storage.getItem(fellBackKey(savedModel))); } catch (_) { return false; }
}
function markFellBackSeen(storage, savedModel) {
  try { if (storage) storage.setItem(fellBackKey(savedModel), '1'); } catch (_) { /* shown again next time */ }
}

// The engine check's failure → which page: any 403 is v1 (the engine is v1, the feature is off, or
// the user is denied; v1 and v2 sit behind the same role and permission gate, and v1 shows its
// own state), anything else is v1 under the "couldn't check" banner.
function pageForProbeError(err) {
  return err && err.status === 403 ? 'v1' : 'unknown';
}

// A fresh idempotency key for POST /v2/runs (the route accepts [A-Za-z0-9_-]{8,80}).
function newClientRequestId(cryptoImpl) {
  const c = cryptoImpl || (typeof crypto !== 'undefined' ? crypto : null);
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  let s = '';
  while (s.length < 32) s += Math.random().toString(36).slice(2);
  return `ne2-${s.slice(0, 32)}`;
}

// The page's first tab from the address: the extension's "More settings" opens
// /?tab=noteexpander&ne=prefs. Anything else, or nothing, is the inbox (null).
const SUB_TABS = ['inbox', 'playground', 'prefs', 'style', 'feedback'];
function subTabFromSearch(search) {
  try {
    const v = new URLSearchParams(String(search || '')).get('ne');
    return SUB_TABS.includes(v) ? v : null;
  } catch { return null; }
}

module.exports = {
  OPTION_LABELS, MODE_LABELS, POLL_BUDGET_MS,
  optionLabel, optionName, modeLabel, formatIsoDate, modelOptionLabel,
  mergeInbox, chunk, isTerminal, copyFor, rowState, nextPollDelay,
  prefsBody, charCount, fieldMessage,
  fellBackKey, fellBackSeen, markFellBackSeen, newClientRequestId, pageForProbeError,
  subTabFromSearch,
};
